"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { cn, getFromNestedObject } from "../utils.js";
import * as __rspack_external_react from "react";
const TAG_VARIANTS = {
    text: 'tw-bg-primary-light-100 tw-text-primary-light-400 dark:tw-bg-primary-dark-500 dark:tw-text-neutral-light-100',
    state: {
        active: 'tw-bg-success-100 tw-text-success-500',
        pending: 'tw-bg-neutral-light-300 tw-text-neutral-light-700',
        hold: 'tw-bg-on-hold-100 tw-text-on-hold-500',
        warning: 'tw-bg-warning-100 tw-text-warning-600',
        info: 'tw-bg-info-100 tw-text-info-500',
        deleted: 'tw-bg-alert-100 tw-text-alert-500'
    },
    user: {
        advanced: 'tw-text-neutral-light-100 tw-bg-info-400',
        intermediate: 'tw-text-neutral-light-800 tw-bg-success-400',
        beginner: 'tw-text-neutral-light-800 tw-bg-warning-400'
    }
};
const TagContext = /*#__PURE__*/ __rspack_external_react.createContext(void 0);
function useTagContext() {
    const context = __rspack_external_react.useContext(TagContext);
    if (!context) throw new Error('Tag compound components must be used within <TagRoot>');
    return context;
}
const TagRoot = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, variant = 'text', children, ...props }, ref)=>/*#__PURE__*/ jsx(TagContext.Provider, {
        value: {
            variant
        },
        children: /*#__PURE__*/ jsx("div", {
            ref: ref,
            className: cn('focus:tw-ring-neutral-950 dark:focus:tw-ring-neutral-300 tw-font-caption tw-inline-flex tw-h-2.4 tw-items-center tw-rounded-full tw-px-1.2 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2', getFromNestedObject(TAG_VARIANTS, variant.split('.')), className),
            ...props,
            children: /*#__PURE__*/ jsx("span", {
                className: "tw-flex tw-items-center tw-justify-center tw-gap-0.8",
                children: children
            })
        })
    }));
TagRoot.displayName = 'TagRoot';
const TagDot = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, ...props }, ref)=>{
    useTagContext();
    return /*#__PURE__*/ jsx("span", {
        ref: ref,
        "aria-hidden": "true",
        className: cn('tw-flex tw-h-0.4 tw-w-0.4 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full', className),
        ...props,
        children: "●"
    });
});
TagDot.displayName = 'TagDot';
const TagText = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, ...props }, ref)=>{
    useTagContext();
    return /*#__PURE__*/ jsx("span", {
        ref: ref,
        className: cn('tw-line-clamp-1', className),
        ...props
    });
});
TagText.displayName = 'TagText';
const TagIcon = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, ...props }, ref)=>{
    useTagContext();
    return /*#__PURE__*/ jsx("span", {
        ref: ref,
        className: cn('tw-flex tw-shrink-0 tw-items-center tw-justify-center', className),
        ...props
    });
});
TagIcon.displayName = 'TagIcon';
const Tag = /*#__PURE__*/ __rspack_external_react.forwardRef(({ variant = 'text', removeDot = false, children, ...props }, ref)=>/*#__PURE__*/ jsxs(TagRoot, {
        ref: ref,
        variant: variant,
        ...props,
        children: [
            variant.includes('state') && !removeDot ? /*#__PURE__*/ jsx(TagDot, {}) : null,
            /*#__PURE__*/ jsx(TagText, {
                children: children
            })
        ]
    }));
Tag.displayName = 'Tag';
export { TAG_VARIANTS, Tag, TagDot, TagIcon, TagRoot, TagText };
